import React from 'react';
import { useNavigate } from 'react-router-dom';
import Modal from '@hero/ui-kit/components/Modal';
import P from '@hero/ui-kit/typography/P';
import useGetUnreadSmsList from '../../api/useGetUnreadSmsList';
import { UnreadSmsItem } from '../../types';
import * as Style from './modal.module.scss';

interface UnreadSmsModalProps {
    onClose: () => void;
}

const formatReceivedAt = (raw: string) => {
    // Backend sends "YYYY-MM-DD HH:mm:ss" in UTC; render in the user's locale.
    const iso = raw.includes('T') ? raw : raw.replace(' ', 'T') + 'Z';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return raw;
    return d.toLocaleString(undefined, {
        month: 'numeric',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit'
    });
};

const UnreadSmsModal: React.FC<UnreadSmsModalProps> = ({ onClose }) => {
    const navigate = useNavigate();
    const { data, isLoading, isError } = useGetUnreadSmsList();

    const items: UnreadSmsItem[] = data ?? [];

    const handleRowClick = (leadId: string) => {
        navigate(`/form-fills/${leadId}`);
        onClose();
    };

    return (
        <Modal isCancelable onClose={onClose} width="page" styles={{ padding: '0' }}>
            <p className={Style.eyebrow}>UNREAD SMS MESSAGES</p>

            <div className={Style.tableWrapper} role="table" aria-label="Unread SMS messages">
                <div className={Style.headerRow} role="row">
                    <span role="columnheader" className={Style.colPhone}>
                        Phone
                    </span>
                    <span role="columnheader" className={Style.colMessage}>
                        Messages
                    </span>
                    <span role="columnheader" className={Style.colTime}>
                        Received at
                    </span>
                </div>

                {isLoading ? (
                    <P size="small" noDefaultMargin className={Style.state}>
                        Loading…
                    </P>
                ) : isError ? (
                    <P size="small" noDefaultMargin className={Style.state}>
                        Couldn’t load unread messages.
                    </P>
                ) : items.length === 0 ? (
                    <P size="small" noDefaultMargin className={Style.state}>
                        No unread SMS.
                    </P>
                ) : (
                    items.map((item) => (
                        <button
                            type="button"
                            key={item.id}
                            role="row"
                            className={Style.dataRow}
                            onClick={() => handleRowClick(item.lead_id)}
                        >
                            <span role="cell" className={Style.colPhone}>
                                {item.lead_phone ?? '—'}
                            </span>
                            <span role="cell" className={Style.colMessage}>
                                <span className={Style.messagePill}>
                                    <span aria-hidden className={Style.unreadDot} />
                                    <span className={Style.messageBody}>{item.body ?? ''}</span>
                                </span>
                            </span>
                            <span role="cell" className={Style.colTime}>
                                {formatReceivedAt(item.created_at)}
                            </span>
                        </button>
                    ))
                )}
            </div>
        </Modal>
    );
};

export default UnreadSmsModal;
